
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { polishPlurals } from "polish-plurals";

@Component
export default class ShopModal extends Vue {
    @Prop({
        type: Boolean,
        required: true,
    })
    public value!: boolean;

    // Product quantities
    public rawsOneQuantity = 0;
    public rawsUltimateQuantity = 0;

    // Product prices
    public rawsOnePrice = 12;
    public rawsUltimatePrice = 120;

    // Current inventory (hardcoded for now)
    public ownedRawsOne = 5;
    public ownedRawsUltimate = 3;
    public ultimateDaysLeft = 15;

    // Purchase state
    public purchasing = false;
    public showLimitDialog = false;
    public showSubscriptionDialog = false;
    public showRawsOneDialog = false;
    public showSummaryDialog = false;
    public polishPlurals = polishPlurals;

    get dialogOpen() {
        return this.value;
    }

    set dialogOpen(val: boolean) {
        if (val) {
            // When modal opens, check if user has RAWS Ultimate and pre-fill quantity
            this.initializeRawsUltimateQuantity();
        }
        this.$emit('input', val);
    }

    get rawsOneTotal() {
        return this.rawsOneQuantity * this.rawsOnePrice;
    }

    get rawsUltimateTotal() {
        return this.rawsUltimateQuantity * this.rawsUltimatePrice;
    }

    get totalPrice() {
        // Only include Ultimate price if it's actually being purchased (not just displayed for existing subscription)
        const ultimatePrice = this.isUltimateChange || (!this.isRawsUltimateActive && this.rawsUltimateQuantity > 0) ? this.rawsUltimateTotal : 0;
        return this.rawsOneTotal + ultimatePrice;
    }

    get hasMixedPurchase() {
        // Check if user is buying RAWS One AND changing Ultimate subscription (including cancellation)
        return this.rawsOneQuantity > 0 && (this.isUltimateChange || this.isUltimateCancellation);
    }

    get ultimateChangePrice() {
        // Calculate the price difference for Ultimate subscription changes
        if (this.isUltimateCancellation) {
            return 0; // Cancellation costs nothing
        } else if (this.isUltimateChange) {
            return this.rawsUltimateTotal; // New subscription amount
        }
        return this.rawsUltimateTotal; // Regular purchase
    }

    get priceDisplayText() {
        // Check if there are no changes at all
        if (!this.hasAnyChanges) {
            return `<div style="display: inline-flex; align-items: baseline; gap: 10px; flex-wrap: wrap; justify-content: center;">
                        <span style="font-size: 14px; color: #666;">Do zapłaty:</span>
                        <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">-</span>
                    </div>`;
        }

        if (this.hasMixedPurchase) {
            // Mixed purchase: show both immediate and future charges
            return `<div style="display: inline-flex; align-items: baseline; gap: 14px; flex-wrap: wrap; justify-content: center;">
                        <span style="font-size: 14px; color: #666;">Do zapłaty:</span>
                        <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">${this.rawsOneTotal} zł</span>
                        <span style="font-size: 14px; color: #666;">Od następnego cyklu rozliczeniowego:</span>
                        <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">${this.ultimateChangePrice} zł</span>
                    </div>`;
        } else if (this.rawsOneQuantity > 0 && !this.isUltimateChange) {
            // RAWS One only or RAWS One + new Ultimate
            return `<div style="display: inline-flex; align-items: baseline; gap: 10px; flex-wrap: wrap; justify-content: center;">
                        <span style="font-size: 14px; color: #666;">Do zapłaty:</span>
                        <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">${this.totalPrice} zł</span>
                    </div>`;
        } else if (this.isUltimateChange && this.rawsOneQuantity === 0) {
            // Ultimate change only
            if (this.isUltimateCancellation) {
                return `<div style="display: inline-flex; align-items: baseline; gap: 10px; flex-wrap: wrap; justify-content: center;">
                            <span style="font-size: 14px; color: #666;">Od następnego cyklu rozliczeniowego:</span>
                            <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">0 zł</span>
                        </div>`;
            } else {
                return `<div style="display: inline-flex; align-items: baseline; gap: 10px; flex-wrap: wrap; justify-content: center;">
                            <span style="font-size: 14px; color: #666;">Od następnego cyklu rozliczeniowego:</span>
                            <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">${this.ultimateChangePrice} zł</span>
                        </div>`;
            }
        } else {
            // Default case
            return `<div style="display: inline-flex; align-items: baseline; gap: 10px; flex-wrap: wrap; justify-content: center;">
                        <span style="font-size: 14px; color: #666;">Do zapłaty:</span>
                        <span class="shop-price-value" style="font-size: 28px; font-weight: bold; color: #ff4081;">${this.totalPrice} zł</span>
                    </div>`;
        }
    }

    get isRawsUltimateActive() {
        return this.ownedRawsUltimate > 0 && this.ultimateDaysLeft > 0;
    }

    get canBuyRawsUltimate() {
        return true; // Always allow buying RAWS ULTIMATE to extend subscription
    }

    get isUltimateChange() {
        // Check if user is changing their Ultimate subscription (already has it and changing quantity from owned amount)
        return this.isRawsUltimateActive && this.rawsUltimateQuantity !== this.ownedRawsUltimate;
    }

    get isUltimateCancellation() {
        // Check if user is cancelling their subscription (setting quantity to 0)
        return this.isRawsUltimateActive && this.rawsUltimateQuantity === 0;
    }

    get hasAnyChanges() {
        // Check if user has made any changes that warrant enabling the DALEJ button
        const hasRawsOneSelection = this.rawsOneQuantity > 0;
        const hasUltimateChanges = this.isRawsUltimateActive ? this.rawsUltimateQuantity !== this.ownedRawsUltimate : this.rawsUltimateQuantity > 0;
        return hasRawsOneSelection || hasUltimateChanges;
    }

    get rawsOneButtonText() {
        // If user is actually changing Ultimate (not just having an existing subscription), show DALEJ, otherwise KUPUJE!
        return (this.isUltimateChange || this.isUltimateCancellation || (!this.isRawsUltimateActive && this.rawsUltimateQuantity > 0)) ? 'DALEJ' : 'PODSUMOWANIE';
    }

    get rawsOneButtonIcon() {
        // If user is actually changing Ultimate (not just having existing subscription), show arrow, otherwise cart
        return (this.isUltimateChange || this.isUltimateCancellation || (!this.isRawsUltimateActive && this.rawsUltimateQuantity > 0)) ? 'mdi-arrow-right' : 'mdi-cart-variant';
    }

    get subscriptionModalTitle() {
        // Change title based on what user is doing with subscription
        if (this.isUltimateCancellation) {
            return 'Anuluj subskrypcję';
        } else if (this.isUltimateChange) {
            return 'Zmiana subskrypcji';
        } else {
            return 'Potwierdzenie subskrypcji';
        }
    }

    get subscriptionPriceLabel() {
        // Change price label for subscription changes
        return this.isUltimateChange ? 'Od następnego cyklu rozliczeniowego:' : 'Do zapłaty:';
    }

    get ultimateModalSubtitle() {
        if (this.isUltimateCancellation) {
            return 'Anulowanie subskrypcji Raws ULTIMATE.';
        } else {
            return 'Raws ULTIMATE to abonament.';
        }
    }

    get ultimateModalText1() {
        if (this.isUltimateCancellation) {
            return '<b>Twoja subskrypcja zostanie anulowana.</b><br>Pakiet będzie aktywny do końca opłaconego okresu, ale nie zostanie automatycznie odnowiony.';
        } else {
            return '<b>Po wykupieniu pakietu będzie on aktywny przez miesiąc.</b><br>Po tym czasie automatycznie pobierzemy opłatę z karty za kolejny miesiąc, jeśli nic nie zmienisz.';
        }
    }

    get ultimateModalText2() {
        if (this.isUltimateCancellation) {
            return '<b>Możesz ponownie aktywować subskrypcję w każdej chwili.</b><br>Wystarczy wrócić do sklepu i wykupić nowy pakiet RAWS Ultimate.';
        } else {
            return '<b>Możesz zrezygnować z abonamentu w dowolnej chwili.</b><br>Jeśli to zrobisz, dalej korzystasz z pakietu do końca opłaconego okresu, a potem subskrypcja po prostu wygasa.';
        }
    }

    get ultimateModalText3() {
        if (this.isUltimateCancellation) {
            return '<b>Anulowanie jest bezpłatne.</b><br>Nie pobieramy żadnych dodatkowych opłat za rezygnację z subskrypcji.';
        } else {
            return '<b>W dowolnej chwili możesz zmienić pakiet na inny.</b><br>Pamiętaj jednak, że nowy plan wejdzie w życie od następnego cyklu rozliczeniowego.';
        }
    }

    get subscriptionButtonColor() {
        // Red color for cancellation, blue for regular subscription actions
        return this.isUltimateCancellation ? '#ff4444' : '#71c5f1';
    }

    get subscriptionButtonText() {
        // Different text for cancellation vs regular actions
        return this.isUltimateCancellation ? 'REZYGNUJ' : 'PODSUMOWANIE';
    }

    get subscriptionButtonIcon() {
        // X icon for cancellation, cart icon for regular actions
        return this.isUltimateCancellation ? 'mdi-close' : 'mdi-cart-variant';
    }

    get subscriptionModalPrice() {
        // Price display in the subscription modal
        if (this.isUltimateCancellation) {
            return '0 zł'; // Cancellation is free
        } else {
            return `${this.rawsUltimateQuantity > 0 ? this.rawsUltimateTotal : this.rawsUltimatePrice} zł`;
        }
    }

    get mainButtonText() {
        // Main DALEJ button text based on action type
        if (this.rawsOneQuantity === 0 && this.isUltimateCancellation) {
            return 'REZYGNUJ'; // Cancellation only
        } else {
            return 'DALEJ'; // Regular purchase/change
        }
    }

    get mainButtonColor() {
        // Main DALEJ button color based on action type
        if (this.rawsOneQuantity === 0 && this.isUltimateCancellation) {
            return '#ff4444'; // Red for cancellation only
        } else {
            return '#ff4081'; // Pink for regular actions
        }
    }

    get mainButtonIcon() {
        // Main DALEJ button icon based on action type
        if (this.rawsOneQuantity === 0 && this.isUltimateCancellation) {
            return 'mdi-close'; // X icon for cancellation only
        } else {
            return 'mdi-arrow-right'; // Arrow for regular actions
        }
    }

    increaseRawsUltimate() {
        this.rawsUltimateQuantity++;
    }

    decreaseRawsUltimate() {
        if (this.rawsUltimateQuantity > 0) {
            this.rawsUltimateQuantity--;
        }
    }

    increaseRawsOne() {
        const totalQuantity = this.ownedRawsOne + this.rawsOneQuantity;
        if (totalQuantity >= 10) {
            this.showLimitDialog = true;
            return;
        }
        if (this.rawsOneQuantity < 10) {
            this.rawsOneQuantity++;
        }
    }

    decreaseRawsOne() {
        if (this.rawsOneQuantity > 0) {
            this.rawsOneQuantity--;
        }
    }

    closeModal() {
        this.dialogOpen = false;
        this.resetQuantities();
    }

    resetQuantities() {
        this.rawsOneQuantity = 0;
        // Reset Ultimate quantity appropriately based on user's subscription status
        // Check ownedRawsUltimate directly instead of computed property
        if (this.ownedRawsUltimate > 0 && this.ultimateDaysLeft > 0) {
            this.rawsUltimateQuantity = this.ownedRawsUltimate;
        } else {
            this.rawsUltimateQuantity = 0;
        }
    }

    async purchaseProducts() {
        // Determine purchase flow based on what user is buying
        const buyingRawsOne = this.rawsOneQuantity > 0;
        const buyingUltimate = this.rawsUltimateQuantity > 0;
        const changingUltimate = this.isUltimateChange;
        const cancellingUltimate = this.isUltimateCancellation;

        if (buyingRawsOne && (buyingUltimate || changingUltimate || cancellingUltimate)) {
            // Both products or RAWS One + Ultimate change/cancel: Show RAWS One modal first
            this.showRawsOneDialog = true;
        } else if (buyingRawsOne && !buyingUltimate && !changingUltimate && !cancellingUltimate) {
            // RAWS One only: Show RAWS One modal
            this.showRawsOneDialog = true;
        } else if (!buyingRawsOne && (buyingUltimate || changingUltimate || cancellingUltimate)) {
            // RAWS Ultimate only (buy/change/cancel): Show Ultimate modal
            this.showSubscriptionDialog = true;
        }
    }

    async processPurchase() {
        this.purchasing = true;

        try {
            // Simulate purchase API call
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Update owned quantities
            this.ownedRawsOne += this.rawsOneQuantity;
            this.ownedRawsUltimate += this.rawsUltimateQuantity;

            // If bought RAWS Ultimate, reset days to 30
            if (this.rawsUltimateQuantity > 0) {
                this.ultimateDaysLeft = 30;
            }

            // Show success message
            this.$emit('purchase-success', {
                rawsOne: this.rawsOneQuantity,
                rawsUltimate: this.rawsUltimateQuantity,
                totalPrice: this.totalPrice
            });

            // Reset and close
            this.resetQuantities();
            this.dialogOpen = false;
            this.showSubscriptionDialog = false;
            this.showRawsOneDialog = false;
            this.showSummaryDialog = false;

        } catch (error) {
            console.error('Purchase failed:', error);
            // Handle error here
        } finally {
            this.purchasing = false;
        }
    }

    cancelSummary() {
        this.showSummaryDialog = false;
    }

    async confirmPurchase() {
        // If this includes a cancellation, handle it specially
        if (this.isUltimateCancellation) {
            await this.processMixedPurchaseWithCancellation();
        } else {
            await this.processPurchase();
        }
    }

    async processCancellation() {
        this.purchasing = true;

        try {
            // Simulate cancellation API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Update subscription status
            this.ownedRawsUltimate = 0;
            this.ultimateDaysLeft = 0;

            // Show success notification
            (this as any).$vToastify.success({
                title: 'Subskrypcja anulowana',
                body: 'Twoja subskrypcja Raws ULTIMATE została pomyślnie anulowana.',
                theme: 'dark'
            });

            // Reset and close
            this.resetQuantities();
            this.dialogOpen = false;

        } catch (error) {
            console.error('Cancellation failed:', error);
            // Handle error here
        } finally {
            this.purchasing = false;
        }
    }

    async processMixedPurchaseWithCancellation() {
        this.purchasing = true;

        try {
            // Simulate mixed purchase + cancellation API call
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Update RAWS One inventory if purchased
            if (this.rawsOneQuantity > 0) {
                this.ownedRawsOne += this.rawsOneQuantity;
            }

            // Cancel the Ultimate subscription
            this.ownedRawsUltimate = 0;
            this.ultimateDaysLeft = 0;

            // Do not show a direct toast here to avoid duplicates; rely on the emitted event below

            // Emit success event
            this.$emit('purchase-success', {
                rawsOne: this.rawsOneQuantity,
                rawsUltimate: 0,
                totalPrice: this.rawsOneTotal,
                cancelled: true
            });

            // Reset and close
            this.resetQuantities();
            this.dialogOpen = false;
            this.showSummaryDialog = false;

        } catch (error) {
            console.error('Mixed purchase with cancellation failed:', error);
            // Handle error here
        } finally {
            this.purchasing = false;
        }
    }

    debugRemoveRawsOne() {
        if (this.ownedRawsOne > 0) {
            this.ownedRawsOne--;
            console.log('Debug: Removed 1 RAWS ONE. Current count:', this.ownedRawsOne);
        } else {
            console.log('Debug: No RAWS ONE to remove.');
        }
    }

    debugRemoveRawsUltimate() {
        if (this.ownedRawsUltimate > 0) {
            this.ownedRawsUltimate--;
            this.ultimateDaysLeft = 0; // Reset days when removing
            console.log('Debug: Removed 1 RAWS ULTIMATE. Current count:', this.ownedRawsUltimate);
        } else {
            console.log('Debug: No RAWS ULTIMATE to remove.');
        }
    }

    cancelSubscription() {
        this.showSubscriptionDialog = false;
    }

    async confirmSubscription() {
        // If cancelling subscription AND not buying RAWS One, process directly without summary
        if (this.isUltimateCancellation && this.rawsOneQuantity === 0) {
            this.showSubscriptionDialog = false;
            await this.processCancellation();
        } else {
            // Show summary modal for all other cases (including mixed purchases with cancellation)
            this.showSubscriptionDialog = false;
            this.showSummaryDialog = true;
        }
    }

    cancelRawsOne() {
        this.showRawsOneDialog = false;
    }

    async confirmRawsOne() {
        // If user is actually changing Ultimate (not just having an existing subscription), show Ultimate modal next
        if (this.isUltimateChange || this.isUltimateCancellation || (!this.isRawsUltimateActive && this.rawsUltimateQuantity > 0)) {
            this.showRawsOneDialog = false;
            this.showSubscriptionDialog = true;
        } else {
            // RAWS One only: Show summary modal
            this.showRawsOneDialog = false;
            this.showSummaryDialog = true;
        }
    }

    mounted() {
        // Initialize Ultimate quantity when component mounts
        this.initializeRawsUltimateQuantity();
    }

    initializeRawsUltimateQuantity() {
        // If user already has RAWS Ultimate, set the quantity to show current plan (actual owned amount)
        // Check ownedRawsUltimate directly instead of computed property that might depend on quantity
        if (this.ownedRawsUltimate > 0 && this.ultimateDaysLeft > 0) {
            this.rawsUltimateQuantity = this.ownedRawsUltimate;
            console.log('Initialized RAWS Ultimate quantity to:', this.ownedRawsUltimate);
        } else {
            this.rawsUltimateQuantity = 0;
            console.log('Initialized RAWS Ultimate quantity to 0 (no active subscription)');
        }
    }
}
